/**
 * customerAccess – reine Logik für die Kundensicht (Mandant / Light-User).
 *
 * Kanzlei-Anforderung: Ein Mandant darf im Cockpit und sonst nirgends
 * Eintragungen vornehmen – ausschließlich in seinem Unfallfragebogen.
 * Einzige Ausnahme: In der Zahlungsübersicht darf er bestätigen, dass er
 * eine Zahlung erhalten hat.
 *
 * Die Rollenerkennung spiegelt `useRoleView()` (Rolle "mandant") und die
 * Backend-Prüfung `session.IsCustomerUser` – fachliche Rollen gewinnen
 * immer gegenüber einer Kundenrolle.
 */

export const CUSTOMER_EDIT_HINT_TITLE = 'Daten nur im Fragebogen änderbar'

export const CUSTOMER_EDIT_HINT_TEXT
  = 'Als Mandant können Sie Ihre Angaben ausschließlich im Unfallfragebogen ändern. '
    + 'Ausnahme: In der Zahlungsübersicht können Sie bestätigen, dass Sie eine Zahlung erhalten haben.'

/** Organisationsrollen, die einen Mandanten kennzeichnen. */
export const CUSTOMER_ORGANIZATION_ROLES = ['Privatkunde', 'Firmenkunde', 'Zeuge'] as const

/** Fachliche Rollen – wer eine davon trägt, ist kein reiner Mandant. */
const PROFESSIONAL_ORGANIZATION_ROLES = [
  'Rechtsanwalt',
  'Sachverständiger',
  'Autohaus',
  'Autovermietung',
  'Abschleppdienst',
  'Versicherung',
] as const

/**
 * Fall-Seiten, die ein Mandant aufrufen darf (Routennamen der Kinder von
 * /faelle/:idCase). Orientiert sich an `CASE_SECTIONS` und
 * `SECTION_VISIBILITY.mandant`: Cockpit, Korrespondenz (Dokumente),
 * Kommunikation/Nachrichten und Termine.
 */
export const CUSTOMER_ALLOWED_CASE_ROUTES: ReadonlySet<string> = new Set([
  'PageCaseDetail',
  'PageCaseDetailDokumente',
  'PageCaseDetailKommunikation',
  'PageCaseDetailNachrichten',
  'PageCaseDetailTermine',
])

/** Ziel, auf das gesperrte Fall-Seiten für Mandanten umgeleitet werden. */
export const CUSTOMER_CASE_FALLBACK_ROUTE = 'PageCaseDetail'

/** Routen, die ein Klick auf eine Cockpit-Kachel für Mandanten NIE öffnet. */
export const CASE_DETAIL_ROUTE_PREFIX = 'PageCaseDetail'

export interface CustomerAccessUser {
  accessTier?: 'full' | 'light' | string | null
  organizationRoles?: readonly string[] | null
}

/**
 * Meldet, ob der Nutzer als Mandant gilt: Light-User immer, sonst nur bei
 * einer Kundenrolle ohne gleichzeitige fachliche Rolle.
 */
export function isCustomerAccount (user: CustomerAccessUser | null | undefined): boolean {
  if (!user) {
    return false
  }
  if (user.accessTier === 'light') {
    return true
  }

  const roles = (user.organizationRoles ?? []).map(r => String(r).trim())
  if (roles.some(r => (PROFESSIONAL_ORGANIZATION_ROLES as readonly string[]).includes(r))) {
    return false
  }
  return roles.some(r => (CUSTOMER_ORGANIZATION_ROLES as readonly string[]).includes(r))
}

/** Meldet, ob der Routenname zu einer Fall-Detailseite gehört. */
export function isCaseDetailRoute (routeName: string | symbol | null | undefined): boolean {
  return typeof routeName === 'string' && routeName.startsWith(CASE_DETAIL_ROUTE_PREFIX)
}

/**
 * Liefert für Mandanten den Routennamen, auf den umgeleitet werden muss,
 * oder `null`, wenn die Route erlaubt ist (bzw. keine Fall-Seite ist).
 */
export function resolveCustomerCaseRedirect (routeName: string | symbol | null | undefined): string | null {
  if (!isCaseDetailRoute(routeName)) {
    return null
  }
  if (CUSTOMER_ALLOWED_CASE_ROUTES.has(routeName as string)) {
    return null
  }
  return CUSTOMER_CASE_FALLBACK_ROUTE
}

/** Kachel-IDs, für die der Hinweis zusätzlich die Zahlungsübersicht anbietet. */
export const CUSTOMER_PAYMENTS_TILE_ID = 'zahlungen'

export function hintOffersPaymentsView (tileId: string | null | undefined): boolean {
  return tileId === CUSTOMER_PAYMENTS_TILE_ID
}
